import React from 'react';
import { ExternalLink, ChevronRight, ChevronDown } from 'lucide-react';
import { safeJson } from '../../../utils/jobMatching.js';
import { SOURCE_LABEL, SOURCE_BADGE_CLASS } from '../../../constants/jobSources.js';
import { HighlightText } from './HighlightText.jsx';
import { ClientTooltip } from './ClientTooltip.jsx';
import { JobDetailRow } from './JobDetailRow.jsx';
import { relativeTime } from './jobFormat.js';

// A single job row: collapsed summary (expandable via click / Enter / Space)
// plus the expanded detail panel. `isLastThree` is forwarded to the client
// tooltip so near-bottom rows open the card upward instead of clipping.
export const JobCard = ({ job, isExpanded, isLastThree, onToggle, filters }) => {
    const skills = Array.isArray(job.skills) ? job.skills : safeJson(job.skills);
    const skillsList = Array.isArray(skills) ? skills : [];
    const client = safeJson(job.client_data) || {};
    const isPaymentVerified = job.payment_verified === 1 || job.payment_verified === true;
    const src = job.source || 'upwork';
    const isFeed = src !== 'upwork';
    // Feed sources carry salaried/range pay as text in client_data (no Upwork
    // gig budget); Upwork carries a numeric budget / hourly rate.
    const salaryText = typeof client.salaryText === 'string' ? client.salaryText : '';

    return (
        <div className={`group transition-colors ${isExpanded ? 'bg-blue-50/10' : 'hover:bg-gray-50'}`}>
            {/* Collapsed Row */}
            <div
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle();
                    }
                }}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 p-2 cursor-pointer sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(11rem,0.5fr)_80px_40px]"
            >
                <div className="flex items-center overflow-hidden basis-full sm:basis-auto min-w-0">
                    <div className="flex-shrink-0 text-gray-400 w-6 flex justify-center">
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </div>
                    <div className="min-w-0 flex items-center gap-2">
                        <h3 className={`text-sm font-medium truncate min-w-0 ${isExpanded ? 'text-blue-600' : 'text-gray-900 group-hover:text-blue-600'}`}>
                            <HighlightText text={job.title} filters={filters} />
                        </h3>
                        <span
                            className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${SOURCE_BADGE_CLASS[src] || SOURCE_BADGE_CLASS.upwork}`}
                            title={`Source: ${SOURCE_LABEL[src] || src}`}
                        >
                            {SOURCE_LABEL[src] || src}
                        </span>
                    </div>
                </div>

                <div
                    className="text-sm font-medium text-green-700 min-w-0 truncate"
                    title={salaryText || undefined}
                >
                    {job.budget
                        ? `$${job.budget}`
                        : (job.min_hourly_rate
                            ? `$${job.min_hourly_rate}-$${job.max_hourly_rate}/hr`
                            : (salaryText
                                ? salaryText
                                : <span className="text-xs text-gray-400 italic font-normal">not specified</span>))}
                </div>

                <div className="text-xs text-gray-500">
                    {relativeTime(job)}
                </div>

                <div className="flex justify-center items-center gap-2">
                    {/* Payment Status Icon — Upwork-only (client verification is
                        an Upwork concept; feed sources have no equivalent). */}
                    {!isFeed && (
                        <ClientTooltip
                            job={job}
                            client={client}
                            isPaymentVerified={isPaymentVerified}
                            isLastThree={isLastThree}
                        />
                    )}

                    <a
                        href={job.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
                <JobDetailRow job={job} client={client} skillsList={skillsList} filters={filters} />
            )}
        </div>
    );
};
